import { useState, useEffect } from 'react';
import { useInstanceStore, type InstanceInfo } from '../../stores/instanceStore';

function DeveloperNameDialog({ onSubmit }: { onSubmit: (name: string) => void }) {
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60">
      <div className="bg-nautical-dark border border-nautical-border rounded-lg p-6 w-80 shadow-xl">
        <h2 className="text-sm font-semibold text-gray-200 mb-3">Welcome to NMEA Studio</h2>
        <p className="text-xs text-gray-400 mb-4">Enter your name to identify your sessions:</p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && name.trim() && onSubmit(name.trim())}
          placeholder="e.g. Tony"
          className="w-full px-3 py-2 bg-nautical-darker border border-nautical-border rounded text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-blue-500"
          autoFocus
        />
        <button
          onClick={() => name.trim() && onSubmit(name.trim())}
          disabled={!name.trim()}
          className="mt-3 w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm rounded transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function CreateInstanceDialog({ onClose, onCreate }: {
  onClose: () => void;
  onCreate: (name: string, visibility: 'public' | 'private') => void;
}) {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-nautical-dark border border-nautical-border rounded-lg p-6 w-80 shadow-xl" onClick={e => e.stopPropagation()}>
        <h2 className="text-sm font-semibold text-gray-200 mb-3">New Instance</h2>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && name.trim() && onCreate(name.trim(), visibility)}
          placeholder="Instance name"
          className="w-full px-3 py-2 bg-nautical-darker border border-nautical-border rounded text-sm text-gray-200 placeholder:text-gray-600 focus:outline-none focus:border-blue-500"
          autoFocus
        />
        <div className="flex items-center gap-3 mt-3">
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input type="radio" checked={visibility === 'public'} onChange={() => setVisibility('public')} className="accent-blue-500" />
            Public
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
            <input type="radio" checked={visibility === 'private'} onChange={() => setVisibility('private')} className="accent-blue-500" />
            Private
          </label>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 bg-nautical-darker text-gray-400 text-sm rounded border border-nautical-border hover:bg-nautical-panel transition-colors">
            Cancel
          </button>
          <button
            onClick={() => name.trim() && onCreate(name.trim(), visibility)}
            disabled={!name.trim()}
            className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm rounded transition-colors"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InstanceTabBar() {
  const {
    developerName,
    setDeveloperName,
    instances,
    activeInstanceId,
    fetchInstances,
    createInstance,
    deleteInstance,
    setActiveInstance,
  } = useInstanceStore();

  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Fetch instances on mount and poll every 5s
  useEffect(() => {
    fetchInstances();
    const timer = setInterval(fetchInstances, 5000);
    return () => clearInterval(timer);
  }, [fetchInstances]);

  // Show name dialog if no developer name set
  if (!developerName) {
    return <DeveloperNameDialog onSubmit={setDeveloperName} />;
  }

  const handleCreate = async (name: string, visibility: 'public' | 'private') => {
    try {
      await createInstance(name, visibility);
      setShowCreateDialog(false);
    } catch (err) {
      console.error('Failed to create instance:', err);
    }
  };

  const handleDelete = async (e: React.MouseEvent, instance: InstanceInfo) => {
    e.stopPropagation();
    if (instance.owner !== developerName) return;
    try {
      await deleteInstance(instance.id);
    } catch (err) {
      console.error('Failed to delete instance:', err);
    }
  };

  return (
    <>
      <div className="h-8 flex items-center bg-[#0a0f1a] border-b border-nautical-border overflow-x-auto">
        {/* Developer name badge */}
        <div className="flex-shrink-0 px-3 text-[10px] text-gray-500 border-r border-nautical-border flex items-center h-full">
          {developerName}
        </div>

        {/* Instance tabs */}
        {instances.map((inst) => {
          const isActive = inst.id === activeInstanceId;
          const isOwner = inst.owner === developerName;
          return (
            <button
              key={inst.id}
              onClick={() => setActiveInstance(inst.id)}
              className={`
                relative flex items-center gap-1.5 h-full px-3 text-xs border-r border-nautical-border transition-colors flex-shrink-0
                ${isActive
                  ? 'bg-nautical-dark text-gray-200 border-t-2 border-t-blue-500'
                  : 'text-gray-500 hover:text-gray-300 hover:bg-nautical-darker border-t-2 border-t-transparent'
                }
              `}
            >
              {/* Status dot */}
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                inst.status === 'running' ? 'bg-green-500' :
                inst.status === 'paused' ? 'bg-yellow-500' :
                'bg-gray-600'
              }`} />

              {/* Name + port */}
              <span className="truncate max-w-[120px]">{inst.name}</span>
              <span className="text-[10px] text-gray-600">:{inst.tcp_port}</span>

              {/* Visibility badge */}
              {inst.visibility === 'private' && (
                <span className="text-[9px] text-yellow-600">priv</span>
              )}

              {/* Close button (owner only) */}
              {isOwner && instances.length > 1 && (
                <span
                  onClick={(e) => handleDelete(e, inst)}
                  className="ml-1 text-gray-600 hover:text-red-400 text-[10px] leading-none"
                >
                  x
                </span>
              )}
            </button>
          );
        })}

        {/* New instance button */}
        <button
          onClick={() => setShowCreateDialog(true)}
          className="flex-shrink-0 h-full px-2 text-gray-600 hover:text-gray-300 text-sm transition-colors"
          title="New instance"
        >
          +
        </button>
      </div>

      {showCreateDialog && (
        <CreateInstanceDialog
          onClose={() => setShowCreateDialog(false)}
          onCreate={handleCreate}
        />
      )}
    </>
  );
}
